import { qsaOptional, qsOptional, qsRequired } from '@/scripts/core/global'
import { type PredictiveSearch } from '@/scripts/interactive-defer/predictive-search'
import { type DetailsModal } from '@/scripts/interactive-defer/details-modal'
import { type HeaderMenu } from '@/scripts/interactive-defer/header-menu'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SELECTORS } from '@/scripts/core/global';
import { toggleHeaderColorScheme } from '@/scripts/core/cart-functions'
import { type StickyColumns } from '@/scripts/theme/sticky-columns'
import { type ProductStickyNav } from '@/scripts/theme/product-sticky-nav'
import { type ProductStickyCta } from '@/scripts/product/product-sticky-cta'
import { type DynamicCollection } from '@/scripts/catalog/dynamic-collection'

export class StickyHeader extends UcoastEl {
	static htmlSelector = 'sticky-header'
	preventHide: boolean = false
	preventReveal: boolean = false
	preventRevealFromAnchor: boolean
	header: HTMLElement
	headerIsAlwaysSticky?: boolean
	headerBounds: DOMRect | {} = {}
	currentScrollTop: number = 0
	predictiveSearch?: PredictiveSearch
	onScrollHandler!: (event: Event) => void
	hideHeaderOnScrollUp!: () => void
	searchModal?: DetailsModal
	isScrolling?: number
	disclosures?: [] | NodeListOf<HeaderMenu>
	announcementBar?: HTMLElement
	stickyColumns?: StickyColumns
	dynamicCollection?: DynamicCollection
	productStickyNav?: ProductStickyNav
	productStickyCta?: ProductStickyCta
	preventRevealFromModal: boolean
	constructor() {
		super()
		this.preventRevealFromAnchor = false
		this.preventRevealFromModal = false
		this.header = qsRequired(SELECTORS.sectionHeader)
		this.announcementBar = qsOptional('[data-announcement-bar]')
		this.stickyColumns = qsOptional('sticky-columns')
		this.productStickyNav = qsOptional('product-sticky-nav')
		this.productStickyCta = qsOptional('product-sticky-cta')
		const dynamicCollection = qsOptional<DynamicCollection>('dynamic-collection')
		if (dynamicCollection && dynamicCollection.dynamicNav) {
			this.dynamicCollection = dynamicCollection
		}
	}

	override connectedCallback() {
		this.header = qsRequired(SELECTORS.sectionHeader)
		this.headerIsAlwaysSticky =
			this.getAttribute('data-sticky-type') === 'always' ||
			this.getAttribute('data-sticky-type') === 'reduce-logo-size'
		this.headerBounds = {}

		this.setHeaderHeight()

		window
			.matchMedia('(max-width: 990px)')
			.addEventListener('change', this.setHeaderHeight.bind(this))

		if (this.headerIsAlwaysSticky && this.header) {
			this.header.classList.add('shopify-section-header-sticky')
		}

		this.currentScrollTop = 0
		this.preventReveal = false
		this.preventRevealFromAnchor = false
		this.predictiveSearch = qsOptional<PredictiveSearch>('predictive-search', this)

		this.onScrollHandler = this.onScroll.bind(this)
		this.hideHeaderOnScrollUp = () => (this.preventReveal = true)

		this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
		window.addEventListener('scroll', this.onScrollHandler, false)

		this.createObserver()
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop
		if (scrollTop > 3) {
			toggleHeaderColorScheme('menu')
		}
	}

	setHeaderHeight() {
	/*	if (!this.header) throw new Error('no header element found')
		const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile'
		document.documentElement.style.setProperty(
			'--header-height',
			`calc(${scaleValue(this.header.offsetHeight, viewport)} * var(--ax))`
		)*/
	}

	override disconnectedCallback() {
		this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
		window.removeEventListener('scroll', this.onScrollHandler)
	}

	createObserver() {
		let observer = new IntersectionObserver((entries, observer) => {
			this.headerBounds = entries[0].intersectionRect
			observer.disconnect()
		})

		observer.observe(this.header)
	}

	beforeAnchorScroll() {
		this.preventRevealFromAnchor = true
	}

	onScroll() {
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop
		if (this.productStickyNav)  {
			this.productStickyNav.onHeaderScrollEvent(scrollTop)
		}
		if (this.productStickyCta) {
			this.productStickyCta.onHeaderScrollEvent(scrollTop, this.currentScrollTop)
		}
		if (this.stickyColumns) {
			this.stickyColumns.onHeaderScrollEvent(scrollTop)
		}
		if (this.dynamicCollection)  {
			this.dynamicCollection.onHeaderScrollEvent()
		}

		if (this.predictiveSearch && this.predictiveSearch.isOpen) return

		if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop > this.currentScrollTop &&
			scrollTop > 10 && // this.headerBounds.bottom
			Math.abs(scrollTop - this.currentScrollTop) > 1
		) {
			this.header.classList.add('scrolled-past-header')
			if (this.preventHide) return
			requestAnimationFrame(this.hide.bind(this))
		} else if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop < this.currentScrollTop &&
			scrollTop > 0
		) {
			this.header.classList.add('scrolled-past-header')
			if (!this.preventReveal && !this.preventRevealFromAnchor) {
				toggleHeaderColorScheme('menu')
				requestAnimationFrame(this.reveal.bind(this))
			} else {
				window.clearTimeout(this.isScrolling)

				this.isScrolling = setTimeout(() => {
					this.preventReveal = false
					this.preventRevealFromAnchor = false
				}, 66)

				requestAnimationFrame(this.hide.bind(this))
			}
		} else if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop <= this.headerBounds.top
		) {
			this.header.classList.remove('scrolled-past-header')
			toggleHeaderColorScheme('starting')
			requestAnimationFrame(this.reset.bind(this))
		} else if (!(this.headerBounds instanceof DOMRectReadOnly)) {
			console.warn('onScroll used in sticky-header but headerBounds is not initialized')
		}

		this.currentScrollTop = scrollTop
	}

	hide() {
		if (this.headerIsAlwaysSticky) return
		document.body.classList.add('shopify-section-header-hidden')
		document.body.classList.add('shopify-section-header-animated')
		this.header.classList.add('shopify-section-header-sticky')
		this.closeMenuDisclosure()
		this.closeSearchModal()
	}

	reveal() {
		if (this.headerIsAlwaysSticky) return
		toggleHeaderColorScheme('menu')
		this.header.classList.add('shopify-section-header-sticky', 'animate')
		document.body.classList.remove('shopify-section-header-hidden')
		document.body.classList.add('shopify-section-header-animated')
	}

	reset() {
		if (this.headerIsAlwaysSticky) return
		document.body.classList.remove('shopify-section-header-hidden')
		this.header.classList.remove(
			'shopify-section-header-sticky',
			'animate'
		)
	}

	closeMenuDisclosure() {
		this.disclosures = this.disclosures || qsaOptional<HeaderMenu>('header-menu', this.header)
		this.disclosures?.forEach((disclosure) => {
			disclosure.close()
		})
	}

	closeSearchModal() {
		this.searchModal =
			this.searchModal ?? qsRequired<DetailsModal>('details-modal', this.header)
		this.searchModal.close(false)
	}
}

